// Zone height management extracted from OfferPageBuilderSimple.js lines 658-965
import { useCallback, useEffect } from 'react';
import { ArrowUpward as MoveUpIcon, KeyboardArrowDown as MoveDownIcon } from '@mui/icons-material';
import React from 'react';

// Standard mobile phone specifications (1080×2340 logical, 405×878 display)
export const STANDARD_MOBILE = {
  // Logical dimensions (for calculations)
  logicalWidth: 1080,
  logicalHeight: 2340,
  safeAreaTop: 100, // Status bar and notch area
  safeAreaBottom: 80, // Home indicator area
  usableHeight: 2160, // logicalHeight - safeAreaTop - safeAreaBottom

  // Display dimensions (~37.5% scale for UI)
  displayWidth: 405, // 1080 * 0.375
  displayHeight: 878, // 2340 * 0.375
  displaySafeAreaTop: 38, // 100 * 0.375 (rounded)
  displaySafeAreaBottom: 30, // 80 * 0.375
  displayUsableHeight: 810, // 2160 * 0.375

  // Scale factor
  scale: 0.375 // 405/1080
};

export const useZoneHeight = ({
  zoneHeightModes,
  setZoneHeightModes,
  heightOverflowAlerts,
  setHeightOverflowAlerts,
  elements,
}) => {
  // Height calculation based on mode
  const getZoneHeight = (zoneId, defaultHeight) => {
    const mode = zoneHeightModes[zoneId] || 'third';
    switch (mode) {
      case 'third':
        return '33vh';
      case 'half':
        return '50vh';
      case 'twoThirds':
        return '67vh';
      case 'full':
        return '100vh';
      default:
        return defaultHeight || '60px';
    }
  };

  // Get height style object based on standard mobile dimensions
  const getZoneHeightStyle = (zoneId, defaultHeight) => {
    // Only apply custom heights if the zone has been explicitly set to a mode
    if (!zoneHeightModes.hasOwnProperty(zoneId)) {
      return { minHeight: defaultHeight || '60px' };
    }

    const mode = zoneHeightModes[zoneId];

    // Calculate heights based on logical 2340px full height (but display at 37.5% scale)
    const thirdHeight = Math.floor(STANDARD_MOBILE.logicalHeight / 3); // 1/3 = 780px logical (293px display)
    const halfHeight = Math.floor(STANDARD_MOBILE.logicalHeight / 2); // 1/2 = 1170px logical (439px display)
    const twoThirdsHeight = Math.floor(STANDARD_MOBILE.logicalHeight * 2 / 3); // 2/3 = 1560px logical (585px display)
    const fullHeight = STANDARD_MOBILE.logicalHeight; // Full = 2340px logical (878px display)

    // For display, we scale down by 37.5%
    const displayThirdHeight = Math.floor(thirdHeight * STANDARD_MOBILE.scale);
    const displayHalfHeight = Math.floor(halfHeight * STANDARD_MOBILE.scale);
    const displayTwoThirdsHeight = Math.floor(twoThirdsHeight * STANDARD_MOBILE.scale);
    const displayFullHeight = Math.floor(fullHeight * STANDARD_MOBILE.scale);

    switch (mode) {
      case 'third':
        return {
          height: `${displayThirdHeight}px`, // 293px display (represents 780px logical)
          maxHeight: `${displayThirdHeight}px`,
          border: '2px solid #4caf50' // Green border to indicate 1/3 mode
        };
      case 'half':
        return {
          height: `${displayHalfHeight}px`, // 439px display (represents 1170px logical)
          maxHeight: `${displayHalfHeight}px`,
          border: '2px solid #ff9800' // Orange border to indicate 1/2 mode
        };
      case 'twoThirds':
        return {
          height: `${displayTwoThirdsHeight}px`, // 585px display (represents 1560px logical)
          maxHeight: `${displayTwoThirdsHeight}px`,
          border: '2px solid #9c27b0' // Purple border to indicate 2/3 mode
        };
      case 'full':
        return {
          height: `${displayFullHeight}px`, // 878px display (represents 2340px logical)
          maxHeight: `${displayFullHeight}px`,
          border: '2px solid #f44336' // Red border to indicate full mode
        };
      default:
        return { minHeight: defaultHeight || '60px' };
    }
  };

  // Check if content exceeds zone height
  const checkZoneHeightOverflow = useCallback((zoneId) => {
    const mode = zoneHeightModes[zoneId];
    if (!mode) return false; // No height mode set, no overflow possible

    // Get the zone element
    const zoneElement = document.querySelector(`[data-zone-id="${zoneId}"]`);
    if (!zoneElement) return false;

    // Get zone height limits
    const thirdHeight = Math.floor(STANDARD_MOBILE.logicalHeight / 3 * STANDARD_MOBILE.scale); // 293px
    const halfHeight = Math.floor(STANDARD_MOBILE.logicalHeight / 2 * STANDARD_MOBILE.scale); // 439px
    const twoThirdsHeight = Math.floor(STANDARD_MOBILE.logicalHeight * 2 / 3 * STANDARD_MOBILE.scale); // 585px
    const fullHeight = Math.floor(STANDARD_MOBILE.logicalHeight * STANDARD_MOBILE.scale); // 878px

    let maxHeight;
    switch (mode) {
      case 'third':
        maxHeight = thirdHeight;
        break;
      case 'half':
        maxHeight = halfHeight;
        break;
      case 'twoThirds':
        maxHeight = twoThirdsHeight;
        break;
      case 'full':
        maxHeight = fullHeight;
        break;
      default:
        return false;
    }

    // Get actual content height
    const contentHeight = zoneElement.scrollHeight;
    const hasOverflow = contentHeight > maxHeight;

    // Update overflow state
    setHeightOverflowAlerts(prev => ({
      ...prev,
      [zoneId]: {
        hasOverflow,
        overflowHeight: hasOverflow ? contentHeight - maxHeight : 0,
        contentHeight,
        maxHeight,
        mode
      }
    }));

    return hasOverflow;
  }, [zoneHeightModes, setHeightOverflowAlerts]);

  // Clear overflow alert for a zone
  const clearOverflowAlert = useCallback((zoneId) => {
    setHeightOverflowAlerts(prev => {
      const newAlerts = { ...prev };
      delete newAlerts[zoneId];
      return newAlerts;
    });
  }, []);

  // Get overflow message for a zone
  const getOverflowMessage = (zoneId) => {
    const alert = heightOverflowAlerts[zoneId];
    if (!alert || !alert.hasOverflow) return null;

    const modeNames = {
      'third': '1/3 screen',
      'half': '1/2 screen',
      'twoThirds': '2/3 screen',
      'full': 'full screen'
    };

    return `Content in this zone exceeds ${modeNames[alert.mode]} height by ${alert.overflowHeight}px. Current content: ${alert.contentHeight}px, Max allowed: ${alert.maxHeight}px.`;
  };

  // Check if any zone has overflow for canvas alert
  const getCanvasOverflowAlert = () => {
    const overflowingZones = Object.keys(heightOverflowAlerts).filter(
      zoneId => heightOverflowAlerts[zoneId]?.hasOverflow
    );

    if (overflowingZones.length === 0) return null;

    if (overflowingZones.length === 1) {
      const alert = heightOverflowAlerts[overflowingZones[0]];
      const modeNames = { 'third': '1/3', 'half': '1/2', 'twoThirds': '2/3', 'full': 'full' };
      const modeName = modeNames[alert.mode] || alert.mode;
      return `⚠️ Zone content exceeds ${modeName} screen height (+${alert.overflowHeight}px)`;
    } else {
      return `⚠️ ${overflowingZones.length} zones exceed height limits`;
    }
  };

  // Toggle zone height mode
  const toggleZoneHeight = (zoneId) => {
    setZoneHeightModes(prev => {
      const currentMode = prev[zoneId] || 'third';
      let nextMode;
      switch (currentMode) {
        case 'third':
          nextMode = 'half';
          break;
        case 'half':
          nextMode = 'twoThirds';
          break;
        case 'twoThirds':
          nextMode = 'full';
          break;
        case 'full':
          nextMode = 'third';
          break;
        default:
          nextMode = 'third';
      }
      return { ...prev, [zoneId]: nextMode };
    });
  };

  // Get expand button icon based on current mode
  const getExpandIcon = (zoneId) => {
    const mode = zoneHeightModes[zoneId] || 'third';
    switch (mode) {
      case 'third':
        return <MoveDownIcon />; // Down arrow: can expand height (increase)
      case 'half':
        return <MoveDownIcon />; // Down arrow: can still expand height (increase to 2/3)
      case 'twoThirds':
        return <MoveDownIcon />; // Down arrow: can still expand height (increase to full)
      case 'full':
        return <MoveUpIcon />; // Up arrow: can only collapse height (decrease)
      default:
        return <MoveDownIcon />;
    }
  };

  // Get tooltip text for current mode
  const getHeightTooltip = (zoneId) => {
    if (!zoneHeightModes.hasOwnProperty(zoneId)) {
      return 'Click to enable dynamic height (1080×2340 logical screen)';
    }

    const thirdHeight = 780; // 2340 / 3 (logical)
    const halfHeight = 1170; // 2340 / 2 (logical)
    const twoThirdsHeight = 1560; // 2340 * 2/3 (logical)
    const fullHeight = 2340; // Full height (logical)

    const mode = zoneHeightModes[zoneId];
    switch (mode) {
      case 'third':
        return `Current: 1/3 screen height (${thirdHeight}px logical). Click to expand to 1/2`;
      case 'half':
        return `Current: 1/2 screen height (${halfHeight}px logical). Click to expand to 2/3`;
      case 'twoThirds':
        return `Current: 2/3 screen height (${twoThirdsHeight}px logical). Click to expand to full`;
      case 'full':
        return `Current: Full screen height (${fullHeight}px logical). Click to collapse to 1/3`;
      default:
        return 'Click to cycle zone height';
    }
  };

  // Get height mode display text
  const getHeightModeText = (zoneId) => {
    if (!zoneHeightModes.hasOwnProperty(zoneId)) {
      return '';
    }

    const thirdHeight = 780; // 2340 / 3 (logical)
    const halfHeight = 1170; // 2340 / 2 (logical)
    const twoThirdsHeight = 1560; // 2340 * 2/3 (logical)
    const fullHeight = 2340; // Full height (logical)

    const mode = zoneHeightModes[zoneId];
    switch (mode) {
      case 'third':
        return ` [1/3: ${thirdHeight}px]`;
      case 'half':
        return ` [1/2: ${halfHeight}px]`;
      case 'twoThirds':
        return ` [2/3: ${twoThirdsHeight}px]`;
      case 'full':
        return ` [Full: ${fullHeight}px]`;
      default:
        return '';
    }
  };

  // Monitor elements and zone height modes for overflow detection
  useEffect(() => {
    // Use a short delay to allow DOM to update
    const timeoutId = setTimeout(() => {
      Object.keys(zoneHeightModes).forEach(zoneId => {
        checkZoneHeightOverflow(zoneId);
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [elements, zoneHeightModes, checkZoneHeightOverflow]);

  // Monitor window resize for overflow recalculation
  useEffect(() => {
    const handleResize = () => {
      Object.keys(zoneHeightModes).forEach(zoneId => {
        checkZoneHeightOverflow(zoneId);
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [zoneHeightModes, checkZoneHeightOverflow]);

  return {
    getZoneHeight,
    getZoneHeightStyle,
    checkZoneHeightOverflow,
    clearOverflowAlert,
    getOverflowMessage,
    getCanvasOverflowAlert,
    toggleZoneHeight,
    getExpandIcon,
    getHeightTooltip,
    getHeightModeText,
    STANDARD_MOBILE,
  };
};
